<template>
  <div class="fleet-security">
    <header class="fleet-security__head">
      <div>
        <h1>Security</h1>
        <p>
          Every failing audit check, live intrusion detection and block decision
          across the estate — the question you could previously only ask one
          server at a time.
        </p>
      </div>
    </header>

    <p v-if="loadError" class="fleet-security__error">{{ loadError }}</p>

    <div class="fleet-security__summary">
      <div class="fleet-security__sum" :class="{ 'fleet-security__sum--bad': summary.findings > 0 }">
        <div class="fleet-security__sum-value">{{ summary.findings ?? '—' }}</div>
        <div class="fleet-security__sum-label">Audit findings</div>
        <div class="fleet-security__sum-sub">
          across {{ summary.servers_with_findings ?? 0 }} of {{ summary.servers_total ?? 0 }} servers
        </div>
      </div>
      <div class="fleet-security__sum" :class="{ 'fleet-security__sum--bad': summary.detections > 0 }">
        <div class="fleet-security__sum-value">{{ summary.detections ?? '—' }}</div>
        <div class="fleet-security__sum-label">Open detections</div>
        <div class="fleet-security__sum-sub">intrusion engine, unresolved</div>
      </div>
      <div class="fleet-security__sum">
        <div class="fleet-security__sum-value">{{ summary.servers_audited ?? '—' }}/{{ summary.servers_total ?? '—' }}</div>
        <div class="fleet-security__sum-label">Servers audited</div>
        <div class="fleet-security__sum-sub">have a stored security.audit result</div>
      </div>
      <div class="fleet-security__sum">
        <div class="fleet-security__sum-value">{{ blockEvents.length }}</div>
        <div class="fleet-security__sum-label">Recent block events</div>
        <div class="fleet-security__sum-sub">{{ simulatedCount }} simulated (monitor mode)</div>
      </div>
    </div>

    <section class="fleet-security__panel">
      <header class="fleet-security__panel-head">Open detections</header>
      <p v-if="loading && !detections.length" class="fleet-security__empty">Loading…</p>
      <p v-else-if="!detections.length" class="fleet-security__empty">
        No open intrusion detections. Findings auto-resolve when the activity stops.
      </p>
      <router-link
        v-for="row in detections"
        :key="row.alert_id"
        class="fleet-security__row"
        :to="row.href"
      >
        <span class="fleet-security__stripe" :class="`is-${severityTone(row.severity)}`"></span>
        <span class="fleet-security__row-body">
          <span class="fleet-security__row-title">{{ row.message }}</span>
          <span class="fleet-security__row-meta">
            <span v-if="row.server">{{ row.server }}</span>
            <span v-if="row.detection_rule_id"> · {{ row.detection_rule_id }}</span>
            <span v-if="row.detail"> · {{ row.detail }}</span>
          </span>
        </span>
        <span class="fleet-security__pill" :class="`is-${severityTone(row.severity)}`">
          {{ severityLabel(row.severity) }}
        </span>
      </router-link>
    </section>

    <section class="fleet-security__panel">
      <header class="fleet-security__panel-head">Audit findings</header>
      <p v-if="loading && !findings.length" class="fleet-security__empty">Loading…</p>
      <p v-else-if="!findings.length" class="fleet-security__empty">
        No failing checks. Servers with no stored audit result aren't counted as
        passing — see "Servers audited" above.
      </p>
      <router-link
        v-for="(row, index) in findings"
        :key="`${row.server_id}-${row.check_id}-${index}`"
        class="fleet-security__row"
        :to="row.href"
      >
        <span class="fleet-security__stripe" :class="`is-${severityTone(row.severity)}`"></span>
        <span class="fleet-security__row-body">
          <span class="fleet-security__row-title">{{ row.label }}</span>
          <span class="fleet-security__row-meta">
            <span>{{ row.server }}</span>
            <span v-if="row.detail"> · {{ row.detail }}</span>
          </span>
        </span>
        <span class="fleet-security__status">{{ row.status }}</span>
        <span class="fleet-security__pill" :class="`is-${severityTone(row.severity)}`">
          {{ severityLabel(row.severity) }}
        </span>
      </router-link>
    </section>

    <!-- Plan 012 §8.4 / Alerts-workspace brief Q1 — accepted checks, read-
         only; the register itself lives on the Alerts page. -->
    <section v-if="accepted.length" class="fleet-security__panel">
      <header class="fleet-security__panel-head">
        Accepted <span class="fleet-security__head-count">{{ accepted.length }}</span>
        <router-link class="fleet-security__head-link" to="/servers/alerts?tab=accepted">Manage in Alerts → Accepted</router-link>
      </header>
      <router-link
        v-for="(row, index) in accepted"
        :key="`acc-${row.server_id}-${row.check_id}-${index}`"
        class="fleet-security__row"
        :to="row.href"
      >
        <span class="fleet-security__stripe is-info"></span>
        <span class="fleet-security__row-body">
          <span class="fleet-security__row-title">{{ row.label }}</span>
          <span class="fleet-security__row-meta">
            <span>{{ row.server }}</span>
            <span> · accepted</span>
            <span v-if="row.reason"> · "{{ row.reason }}"</span>
          </span>
        </span>
        <span class="fleet-security__status">{{ row.status }}</span>
      </router-link>
    </section>

    <section class="fleet-security__panel">
      <header class="fleet-security__panel-head">Recent block events</header>
      <p v-if="!blockEvents.length && !loading" class="fleet-security__empty">
        No block events recorded. Fleet block rules ship in monitor mode, so this
        fills with "would have blocked" decisions before anything is enforced.
      </p>
      <div
        v-for="event in blockEvents"
        :key="event.id"
        class="fleet-security__row fleet-security__row--static"
      >
        <span class="fleet-security__stripe" :class="event.simulated ? 'is-info' : 'is-warn'"></span>
        <span class="fleet-security__row-body">
          <span class="fleet-security__row-title">
            {{ event.action }} <template v-if="event.ip">· {{ event.ip }}</template>
          </span>
          <span class="fleet-security__row-meta">
            <span v-if="event.server">{{ event.server }}</span>
            <span v-if="event.detection_rule_id"> · {{ event.detection_rule_id }}</span>
            <span v-if="event.backend"> · {{ event.backend }}</span>
            <span v-if="event.detail"> · {{ event.detail }}</span>
          </span>
        </span>
        <span class="fleet-security__pill" :class="event.simulated ? 'is-info' : 'is-warn'">
          {{ event.simulated ? 'Simulated' : 'Enforced' }}
        </span>
      </div>
    </section>
  </div>
</template>

<script setup>
/**
 * Fleet-wide Security — mounted at `/security` by
 * `app\module\observability\Controller\FleetDisplayController::security()`.
 *
 * The 2026-08-01 UI review §02 promoted this out of the per-server detail
 * tabs: "'show me every failing check across the estate' is a question you
 * cannot currently ask." The per-server Security tab is unchanged and is where
 * every row here links to — this view exists to find the row, not to replace
 * the place you act on it.
 *
 * Deliberately three separate panels rather than one merged queue, unlike the
 * Overview's "Needs attention": that panel merges because an operator
 * triaging their morning doesn't think in modules, whereas someone who has
 * navigated to Security specifically IS thinking in kinds — "what's failing"
 * and "who is attacking" are different investigations and interleaving them
 * would help neither.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';

const props = defineProps({
  ...commonProps,
  apiBase: { type: String, default: '/api/v1/observability/fleet' },
});

const POLL_MS = 30000;

const api = useApi();
const summary = ref({});
const findings = ref([]);
const accepted = ref([]);
const detections = ref([]);
const blockEvents = ref([]);
const loading = ref(true);
const loadError = ref(null);
let pollHandle = null;

async function load() {
  try {
    const data = await api.get(`${props.apiBase}/security`);
    summary.value = data.summary || {};
    findings.value = data.findings || [];
    accepted.value = data.accepted || [];
    detections.value = data.detections || [];
    blockEvents.value = data.block_events || [];
    loadError.value = null;
  } catch {
    loadError.value = 'Could not load fleet security — check your connection and try again.';
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  load();
  pollHandle = setInterval(load, POLL_MS);
});

onBeforeUnmount(() => {
  if (pollHandle) {
    clearInterval(pollHandle);
  }
});

const simulatedCount = computed(() => blockEvents.value.filter((e) => e.simulated).length);

function severityTone(severity) {
  if (severity === 'critical' || severity === 'serious' || severity === 'high') return 'bad';
  if (severity === 'warning' || severity === 'medium') return 'warn';
  return 'info';
}

function severityLabel(severity) {
  return severity ? severity.charAt(0).toUpperCase() + severity.slice(1) : 'Unknown';
}
</script>

<style lang="scss">
.fleet-security {
  &__head {
    margin-bottom: 18px;

    h1 { font-size: 20px; font-weight: 600; margin: 0 0 4px; }
    p { font-size: 13px; color: var(--theme-text-secondary); margin: 0; max-width: 74ch; }
  }

  &__error {
    background: #f8dede;
    color: #a53232;
    border-radius: var(--theme-radius, 6px);
    padding: 10px 14px;
    font-size: 12.5px;
    margin-bottom: 14px;
  }

  &__summary {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 16px;

    @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  }

  &__sum {
    position: relative;
    overflow: hidden;
    padding: 13px 16px;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);

    &::before {
      content: "";
      position: absolute;
      left: 0; top: 0; bottom: 0;
      width: 3px;
      background: var(--theme-border-strong);
    }

    &--bad::before { background: var(--theme-status-danger); }
    &--bad .fleet-security__sum-value { color: #a53232; }
  }

  &__sum-value {
    font-size: 22px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
  }

  &__sum-label { font-size: 11.5px; color: var(--theme-text-secondary); margin-top: 2px; }
  &__sum-sub {
    font-family: var(--theme-font-mono);
    font-size: 10px;
    color: var(--theme-text-tertiary);
    margin-top: 4px;
  }

  &__panel {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    overflow: hidden;
    margin-bottom: 14px;
  }

  &__panel-head {
    padding: 11px 14px;
    border-bottom: 1px solid var(--theme-border);
    font-size: 12.5px;
    font-weight: 620;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__head-count {
    font-weight: 400;
    color: var(--theme-text-tertiary);
  }

  &__head-link {
    margin-left: auto;
    font-size: 11.5px;
    font-weight: 600;
    color: var(--theme-accent-dark);
    text-decoration: none;
  }

  &__empty {
    padding: 16px 14px;
    margin: 0;
    font-size: 12.5px;
    color: var(--theme-text-tertiary);
  }

  &__row {
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 9px 14px;
    border-bottom: 1px solid var(--theme-border-soft);
    font-size: 12.5px;
    color: inherit;
    text-decoration: none !important;

    &:last-child { border-bottom: 0; }
    &:not(&--static):hover { background: var(--theme-bg-content); }
  }

  &__stripe {
    width: 3px;
    align-self: stretch;
    border-radius: 2px;
    flex: none;

    &.is-bad  { background: var(--theme-status-danger); }
    &.is-warn { background: var(--theme-status-warning); }
    &.is-info { background: var(--theme-status-info); }
  }

  &__row-body { flex: 1; min-width: 0; }

  &__row-title {
    display: block;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__row-meta {
    display: block;
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 2px;
  }

  &__status {
    flex: none;
    font-family: var(--theme-font-mono);
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--theme-text-tertiary);
  }

  &__pill {
    flex: none;
    font-size: 9.5px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    padding: 2.5px 8px;
    border-radius: 20px;
    font-weight: 700;
    white-space: nowrap;

    &.is-bad  { background: #fbe9e6; color: #a53232; }
    &.is-warn { background: #fdf1de; color: #8a5a0d; }
    &.is-info { background: var(--theme-border-soft); color: var(--theme-text-secondary); }
  }
}
</style>
